/* eslint-disable max-lines */
"use client";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import isEmpty from "lodash.isempty";
import React, { useState, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setClientCurrentClickedItem, setClientVariantsLabel } from "../../../store/slices/pdpSlice";
import { constants, labelStyle } from "../../../constants/pageConstants/pdpConstants";
import Typography from "../../atoms/typography";
import CustomImage from "../../atoms/customImage";
import { debounce, updateCurrency } from "../../../utils/common";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import { changeVariantGaEvent } from "../../../analytics/gaEvents";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { COUNTRIES, CURRENCY_ID } from "../../../constants/common/common";
import {
  requestToFetchBlockedDatesAction,
  requestToFetchDeliveryDatesAction,
  requestToFetchInternationalShippingMethod,
  setDeliveryDetailsAction,
} from "../../../store/slices/common/shippingDateAndSlotSelection";
import { resetPdpAddOnState } from "../../../store/slices/pdpAddonSlice";
import useProductAnalyticsPayload from "../../../customHooks/pdp/useProductAnalyticsPayload";
import { isSupplierFlowEnabled } from "../../../utils/api/controlProxy";

const ProductPreview = dynamic(() => import("../ProductPreview"), { ssr: false });
const Drawer = dynamic(() => import("../drawer"), { ssr: false });

const FlavorTypes = dynamic(() => import("../flavorTypes"), { ssr: false });

/**
 * Variants Components  for rendering variant selection and product display.
 *
 * @returns {React.Component} The Variants Components .
 */
const VariantsComponents = ({ isMobile }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const imageDomain = process.env.NEXT_PUBLIC_ASSET_PREFIX;

  const {
    clientVariants,
    clientVariantLabel,
    clientVariantType,
    flavorList,
    clientVariantFlavour,
    selectedCurrency,
    partyId,
  } = useSelector(
    (state) => ({
      clientVariants: state.pdp.clientVariants,
      clientVariantLabel: state.pdp.clientVariantLabel,
      clientVariantType: state.pdp.clientVariantType,
      flavorList: state.pdp.flavorList,
      clientVariantFlavour: state.pdp.clientVariantFlavour,
      selectedCurrency: state.currencyList?.userSelectedCurrency,
      partyId: state.loginSignUp?.userDetails?.customerID,
    }),
    shallowEqual,
  );

  const {
    commonCleverTapData,
    eventDataForCdp,
    catalogId,
    productId,
    pincode,
    selectedDomesticLocation,
    selectedIntLocation,
    currentSelectedProductId,
  } = useProductAnalyticsPayload();

  const [editCombo, setEditCombo] = useState([]);

  /**
   * Handles the click event for selecting a product image.
   *
   * @param {string} itemKey - The key of the selected variant.
   * @param {Object} extractData - The data extracted from the selected variant.
   */
  const onClickProductImages = useMemo(() => {
    return debounce((itemKey, extractData) => {
      const productIds = extractData?.productId;

      if (productIds === currentSelectedProductId) {
        return;
      }

      dispatch(resetPdpAddOnState());
      dispatch(setClientVariantsLabel(itemKey));
      dispatch(setClientCurrentClickedItem(extractData));

      if (selectedDomesticLocation?.address && pincode && catalogId === COUNTRIES.INDIA) {
        dispatch(
          requestToFetchBlockedDatesAction({
            productId: productIds,
            pinCode: pincode,
          }),
        );
        dispatch(
          requestToFetchDeliveryDatesAction({
            productId: productIds,
            pinCode: pincode,
            currencyUomId: CURRENCY_ID,
            catalogId: extractData?.catalogId,
            countryGeoId: extractData?.countryGeoId,
            partyId: isSupplierFlowEnabled() ? partyId : "",
          }),
        );
      } else if (selectedIntLocation?.pincode && catalogId !== COUNTRIES.INDIA) {
        dispatch(
          requestToFetchInternationalShippingMethod({
            productId: productIds,
            city: selectedIntLocation?.pincode,
          }),
        );
      }

      dispatch(setDeliveryDetailsAction({}));

      // Track events
      trackEvent(GA4_EVENTS?.VARIANT_CHANGED, { ...changeVariantGaEvent(extractData, pincode, productId) }, ["ga4"]);
      trackEvent(cleverTapCommonEvents.variantSelected, { ...commonCleverTapData, is_egg: "" }, ["clevertap"]);
      if (productIds) {
        trackEvent(
          cleverTapCommonEvents?.variantSelected,
          {
            ...eventDataForCdp,
            products: [
              {
                ...eventDataForCdp.products[0],
                productId: productIds,
              },
            ],
          },
          ["CDP"],
        );
      }
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDomesticLocation,
    pincode,
    catalogId,
    selectedIntLocation,
    productId,
    commonCleverTapData,
    currentSelectedProductId,
  ]);

  /**
   * Handles the edit button click event by setting the value for the combo being edited.
   *
   * @param {any} value - The value to set for editing the combo.
   * @returns {void}
   */
  const onHandleEditClick = (value) => {
    setEditCombo(value);
  };

  /**
   * Extracts specific data from the given item based on the provided keys and conditions.
   *
   * @param {Object} item - The item object containing data to extract.
   * @param {string} itemKey - The key to access the relevant data in the item object.
   * @returns {any} - The extracted data based on `flavorList`, `clientVariantType`, or fallback to `itemKey`.
   */
  const retrieveItemVariantData = (item, itemKey) => {
    if (flavorList?.length) {
      return item[itemKey][clientVariantType]?.[clientVariantFlavour];
    }
    if (!isEmpty(clientVariantType)) {
      return item?.[itemKey]?.[clientVariantType];
    }
    return item[itemKey];
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        {!isEmpty(clientVariants) && (
          <>
            <Typography variant="p" Tag="p" classes={labelStyle}>
              {constants.AVAILABLE_COMBOS}
            </Typography>

            <div className="flex gap-10 overflow-y-hidden scrollbar-thin scrollbar-webkit xxl:gap-12" id="pdpVariants">
              {clientVariants?.map((item) => {
                const itemKey = Object.keys(item)?.[0];
                let extractData = retrieveItemVariantData(item, itemKey);
                const imageURL = extractData?.productContent?.productImages?.[0];

                if (extractData) {
                  return (
                    <div
                      onClick={() => onClickProductImages(itemKey, extractData)}
                      key={imageURL}
                      className={`cursor-pointer rounded-[10px] border ${clientVariantLabel == itemKey ? "border-darkOliveGreen bg-fnp-50" : "border-gray-300"} mb-4 w-[25%] min-w-[100px] p-3 duration-300 ease-in-out hover:border-darkOliveGreen hover:bg-fnp-50 md:min-w-[120px] md:p-[5px] xmll:min-w-[136px] xxl:min-w-[148px] xxl:p-[5px]`}
                    >
                      <CustomImage
                        src={imageDomain + imageURL?.replace("/l/", "/s/")}
                        width={100}
                        height={100}
                        isPriority={false}
                        alt={"image"}
                        borderRadius={10}
                        sizes="10vw"
                        customStyle={{ width: "100%", height: "65% xxl:h-[70%]" }}
                      />
                      <div className="pt-4 text-gray-500 xxl:pb-8">
                        <div className="flex justify-between">
                          <div
                            className={`text-10 xxl:text-12 ${clientVariantLabel == itemKey ? "font-500 text-fnp-400" : "font-400 text-grey-500"}`}
                          >
                            {itemKey}
                          </div>
                          {!isEmpty(extractData?.productComboComponents) && (
                            <CustomImage
                              onClick={() => onHandleEditClick(extractData?.productComboComponents)}
                              src={"/icons/edit.svg"}
                              width={14}
                              height={14}
                              isPriority={false}
                              alt={"image"}
                              borderRadius={4.8}
                              sizes="10vw"
                              className="h-max"
                            />
                          )}
                        </div>

                        <div
                          className={`pt-4 text-14 xxl:text-16 ${clientVariantLabel == itemKey ? "font-500 text-fnp-400" : "font-400 text-grey-500"}`}
                        >
                          {updateCurrency(selectedCurrency, extractData?.productPrice?.price)}
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </>
        )}
      </div>
      <Drawer
        styles={"pt-0 pb-16"}
        position={isMobile ? "bottom" : "right"}
        isOpen={!isEmpty(editCombo)}
        onDrawerClose={() => onHandleEditClick([])}
      >
        <div className="flex flex-col justify-between border-gray-200 bg-white-900">
          <Typography
            Tag="p"
            classes="w-full pl-8 flex items-center flex-shrink-0 font-600 text-16 text-fnp-500 border-b py-14"
          >
            {constants.MODIFY_YOUR_ORDER}
          </Typography>
          <>
            {editCombo?.map((item, index) => {
              return (
                <>
                  <ProductPreview
                    key={item?.productId}
                    productImage={item?.productImages?.[0]}
                    productName={item?.productName}
                    price={item?.productPrice.price}
                    listPrice={item.productPrice.listPrice}
                    redirectedURL={item?.productUrl}
                  />
                  {index < editCombo?.length - 1 && <div className="border-b" />}
                </>
              );
            })}
          </>
        </div>
      </Drawer>
      {!isEmpty(flavorList) && (
        <div className={`${isMobile ? "" : "mt-18 border-t-2 border-lightGrey px-0 pt-18"}`}>
          <FlavorTypes flavorList={flavorList} isMobile={isMobile} />
        </div>
      )}
    </>
  );
};

export default React.memo(VariantsComponents);

VariantsComponents.propTypes = { isMobile: PropTypes.bool };
